/**
 * @fileoverview Simple search and replace for CodeMirror toolbar.
 * @module editor/extensions/toolbarSearch
 */

import { EditorView, keymap } from '@codemirror/view';
import type { ViewUpdate } from '@codemirror/view';
import { Prec, StateEffect, StateField } from '@codemirror/state';
import {
  search,
  openSearchPanel,
  closeSearchPanel,
  findNext,
  findPrevious,
  replaceNext,
  replaceAll,
  getSearchQuery,
  setSearchQuery,
  SearchQuery,
  searchPanelOpen as isSearchPanelOpen,
} from '@codemirror/search';
import { isImeComposing } from '../../utils/imeComposing';

// State to track if search panel is open
export const searchPanelOpen = StateField.define<boolean>({
  create: () => false,
  update: (value, tr) => {
    for (const effect of tr.effects) {
      if (effect.is(setSearchPanelOpen)) return effect.value;
    }
    return value;
  },
});

export const setSearchPanelOpen = StateEffect.define<boolean>();

interface SearchPanelControls {
  dom: HTMLElement;
  searchInput: HTMLInputElement;
  replaceInput: HTMLInputElement;
  caseCb: HTMLInputElement;
  wordCb: HTMLInputElement;
  regexpCb: HTMLInputElement;
  countEl: HTMLSpanElement;
  refreshCount: () => void;
  clearNotice: () => void;
}

const MAX_PREFILL_LENGTH = 100;

/**
 * Query text for a selection of up to MAX_PREFILL_LENGTH characters, escaped
 * for the current mode. Both modes read `\n` as a newline, so multi-line
 * selections still match; plain mode also unescapes `\\` and `\t`.
 */
function selectionSearchText(view: EditorView, regexp: boolean): string | null {
  const selection = view.state.selection.main;
  if (selection.empty || selection.to - selection.from > MAX_PREFILL_LENGTH) return null;
  const text = view.state.doc.sliceString(selection.from, selection.to);
  const escaped = regexp ? text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : text.replace(/\\/g, '\\\\');
  return escaped.replace(/\r?\n/g, '\\n');
}

/** Per-view initial focus target, consumed when the search panel is created.
 * WeakMap (not a module global) so two editors opening search in the same
 * tick cannot steal each other's focus. */
const pendingSearchFocusByView = new WeakMap<EditorView, 'search' | 'replace'>();

function focusPanelInput(view: EditorView, target: 'search' | 'replace'): void {
  window.setTimeout(() => {
    if (!view.dom.isConnected) return;
    const selector =
      target === 'replace'
        ? '.cm-toolbar-search-panel .replace-input'
        : '.cm-toolbar-search-panel .search-input';
    const input = view.dom.querySelector<HTMLInputElement>(selector);
    if (!input) return;
    input.focus();
    input.select();
  }, 0);
}

function openToolbarSearchWithFocus(view: EditorView, target: 'search' | 'replace'): boolean {
  pendingSearchFocusByView.set(view, target);
  const previous = getSearchQuery(view.state);
  const selectedText = selectionSearchText(view, previous.regexp);

  view.dispatch({ effects: setSearchPanelOpen.of(true) });
  // openSearchPanel prefills from the selection itself (unescaped, dropping the
  // replace text), so settle the query after it. On an open panel it would also
  // focus the input first, which blocks syncing the query into it.
  if (!isSearchPanelOpen(view.state)) openSearchPanel(view);
  view.dispatch({
    effects: setSearchQuery.of(
      new SearchQuery({
        search: selectedText ?? previous.search,
        caseSensitive: previous.caseSensitive,
        wholeWord: previous.wholeWord,
        regexp: previous.regexp,
        replace: previous.replace,
      })
    ),
  });
  focusPanelInput(view, target);
  return true;
}

export function openToolbarSearch(view: EditorView): boolean {
  return openToolbarSearchWithFocus(view, 'search');
}

export function openToolbarSearchReplace(view: EditorView): boolean {
  return openToolbarSearchWithFocus(view, 'replace');
}

export function closeToolbarSearch(view: EditorView): boolean {
  // Not handled when closed, so Escape reaches the editor's other bindings
  if (!view.state.field(searchPanelOpen) && !isSearchPanelOpen(view.state)) return false;
  view.dispatch({ effects: setSearchPanelOpen.of(false) });
  closeSearchPanel(view);
  return true;
}

/** Jump to the next/previous match, or open search when there is nothing to find yet. */
function stepToolbarSearch(view: EditorView, backwards: boolean): boolean {
  if (!getSearchQuery(view.state).valid) return openToolbarSearch(view);
  if (backwards) findPrevious(view);
  else findNext(view);
  return true;
}

export function toggleToolbarSearch(view: EditorView): boolean {
  const isOpen = view.state.field(searchPanelOpen);
  return isOpen ? closeToolbarSearch(view) : openToolbarSearch(view);
}

const MATCH_COUNT_CAP = 1000;

// Counts with CodeMirror's own cursor so the number always agrees with the highlights
function renderMatchCount(view: EditorView, countEl: HTMLSpanElement): void {
  const query = getSearchQuery(view.state);
  if (!query.search) {
    countEl.style.display = 'none';
    countEl.textContent = '';
    return;
  }

  countEl.style.display = 'inline';
  if (!query.valid) {
    countEl.textContent = 'Invalid regex';
    return;
  }

  const caret = view.state.selection.main.from;
  const cursor = query.getCursor(view.state);
  let total = 0;
  let current = 0;
  for (let next = cursor.next(); !next.done; next = cursor.next()) {
    total++;
    if (!current && next.value.from <= caret && caret < next.value.to) current = total;
    if (total >= MATCH_COUNT_CAP) break;
  }

  const totalLabel = total >= MATCH_COUNT_CAP ? `${MATCH_COUNT_CAP}+` : `${total}`;
  if (total === 0) {
    countEl.textContent = 'No matches';
  } else if (current) {
    countEl.textContent = `${current}/${totalLabel}`;
  } else {
    countEl.textContent = total === 1 ? '1 match' : `${totalLabel} matches`;
  }
}

function queryEquals(a: SearchQuery, b: SearchQuery): boolean {
  return (
    a.search === b.search &&
    a.replace === b.replace &&
    a.caseSensitive === b.caseSensitive &&
    a.wholeWord === b.wholeWord &&
    a.regexp === b.regexp
  );
}

function applyQueryToControls(query: SearchQuery, controls: SearchPanelControls): void {
  if (document.activeElement !== controls.searchInput) {
    controls.searchInput.value = query.search || '';
  }
  if (document.activeElement !== controls.replaceInput) {
    controls.replaceInput.value = query.replace || '';
  }
  controls.caseCb.checked = query.caseSensitive;
  controls.wordCb.checked = query.wholeWord;
  controls.regexpCb.checked = query.regexp;
}

// Create search panel DOM
function createSearchPanelControls(view: EditorView): SearchPanelControls {
  const dom = document.createElement('div');
  dom.className = 'cm-toolbar-search-panel';
  dom.style.cssText = `
    background-color: var(--ai-toolbar-bg);
    border-bottom: 1px solid var(--ai-toolbar-border);
    padding: 8px 12px;
  `;
  dom.addEventListener('mousedown', (e) => {
    e.stopPropagation();
  });

  const container = document.createElement('div');
  container.className = 'search-container';
  container.style.cssText = `
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;
  dom.appendChild(container);

  const searchRow = document.createElement('div');
  searchRow.className = 'search-row';
  searchRow.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
  `;
  container.appendChild(searchRow);

  const replaceRow = document.createElement('div');
  replaceRow.className = 'replace-row';
  replaceRow.style.cssText = `
    display: flex;
    align-items: center;
    gap: 6px;
  `;
  container.appendChild(replaceRow);

  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'search-input';
  searchInput.placeholder = 'Find...';
  searchInput.setAttribute('main-field', 'true');
  searchInput.setAttribute('aria-label', 'Find in editor');
  searchInput.style.cssText = `
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
    font-size: 16px;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    background-color: var(--ai-toolbar-input-bg);
    color: var(--ai-toolbar-text);
    outline: none;
  `;
  searchRow.appendChild(searchInput);

  const prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.className = 'search-btn prev';
  prevBtn.textContent = '^';
  prevBtn.title = 'Previous match (Shift+Enter, Shift+F3)';
  prevBtn.setAttribute('aria-label', 'Previous match');
  prevBtn.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 10px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    background-color: transparent;
    color: var(--ai-toolbar-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  `;
  prevBtn.addEventListener('mouseenter', () => {
    prevBtn.style.backgroundColor = 'var(--ai-toolbar-active-bg)';
    prevBtn.style.borderColor = 'var(--ai-toolbar-active)';
    prevBtn.style.color = 'var(--ai-toolbar-active)';
  });
  prevBtn.addEventListener('mouseleave', () => {
    prevBtn.style.backgroundColor = 'transparent';
    prevBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
    prevBtn.style.color = 'var(--ai-toolbar-text-secondary)';
  });
  searchRow.appendChild(prevBtn);

  const nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.className = 'search-btn next';
  nextBtn.textContent = 'v';
  nextBtn.title = 'Next match (Enter, F3)';
  nextBtn.setAttribute('aria-label', 'Next match');
  nextBtn.style.cssText = prevBtn.style.cssText;
  nextBtn.addEventListener('mouseenter', () => {
    nextBtn.style.backgroundColor = 'var(--ai-toolbar-active-bg)';
    nextBtn.style.borderColor = 'var(--ai-toolbar-active)';
    nextBtn.style.color = 'var(--ai-toolbar-active)';
  });
  nextBtn.addEventListener('mouseleave', () => {
    nextBtn.style.backgroundColor = 'transparent';
    nextBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
    nextBtn.style.color = 'var(--ai-toolbar-text-secondary)';
  });
  searchRow.appendChild(nextBtn);

  const countEl = document.createElement('span');
  countEl.className = 'search-match-count';
  countEl.setAttribute('aria-live', 'polite');
  countEl.style.cssText = `
    font-size: 12px;
    font-weight: 500;
    color: var(--ai-toolbar-text-muted);
    padding: 0 6px;
    white-space: nowrap;
  `;
  searchRow.appendChild(countEl);

  const createOption = (
    cssClass: string,
    text: string,
    tooltip: string,
    ariaLabel: string
  ): { label: HTMLLabelElement; input: HTMLInputElement } => {
    // Look (including the checked state) comes from toolbarSearchTheme so
    // setting `.checked` from code restyles the pill too
    const label = document.createElement('label');
    label.className = 'search-option';
    label.title = tooltip;

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = cssClass;
    input.setAttribute('aria-label', ariaLabel);

    const span = document.createElement('span');
    span.textContent = text;

    label.appendChild(input);
    label.appendChild(span);
    return { label, input };
  };

  const caseOption = createOption('case-sensitive', 'Aa', 'Match case', 'Match case');
  const wordOption = createOption('whole-word', 'ab', 'Whole word only', 'Whole word only');
  const regexOption = createOption('regexp', '.*', 'Use regular expression', 'Use regular expression');
  searchRow.appendChild(caseOption.label);
  searchRow.appendChild(wordOption.label);
  searchRow.appendChild(regexOption.label);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'search-btn close';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close search (Escape)';
  closeBtn.setAttribute('aria-label', 'Close search');
  closeBtn.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    font-size: 18px;
    font-weight: 600;
    border: 1px solid var(--ai-toolbar-input-border);
    border-radius: 6px;
    background-color: transparent;
    color: var(--ai-toolbar-text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
  `;
  closeBtn.addEventListener('mouseenter', () => {
    closeBtn.style.backgroundColor = 'var(--ai-toolbar-active-bg)';
    closeBtn.style.borderColor = 'var(--ai-toolbar-active)';
    closeBtn.style.color = 'var(--ai-toolbar-active)';
  });
  closeBtn.addEventListener('mouseleave', () => {
    closeBtn.style.backgroundColor = 'transparent';
    closeBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
    closeBtn.style.color = 'var(--ai-toolbar-text-secondary)';
  });
  searchRow.appendChild(closeBtn);

  const replaceInput = document.createElement('input');
  replaceInput.type = 'text';
  replaceInput.className = 'replace-input';
  replaceInput.placeholder = 'Replace...';
  replaceInput.setAttribute('aria-label', 'Replace in editor');
  replaceInput.style.cssText = searchInput.style.cssText;
  replaceRow.appendChild(replaceInput);

  const replaceBtn = document.createElement('button');
  replaceBtn.type = 'button';
  replaceBtn.className = 'search-btn replace';
  replaceBtn.textContent = 'Replace';
  replaceBtn.title = 'Replace current match (Enter in the Replace box)';
  replaceBtn.setAttribute('aria-label', 'Replace current match');
  replaceBtn.style.cssText = prevBtn.style.cssText;
  replaceBtn.addEventListener('mouseenter', () => {
    replaceBtn.style.backgroundColor = 'var(--ai-toolbar-active-bg)';
    replaceBtn.style.borderColor = 'var(--ai-toolbar-active)';
    replaceBtn.style.color = 'var(--ai-toolbar-active)';
  });
  replaceBtn.addEventListener('mouseleave', () => {
    replaceBtn.style.backgroundColor = 'transparent';
    replaceBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
    replaceBtn.style.color = 'var(--ai-toolbar-text-secondary)';
  });
  replaceRow.appendChild(replaceBtn);

  const replaceAllBtn = document.createElement('button');
  replaceAllBtn.type = 'button';
  replaceAllBtn.className = 'search-btn replace-all';
  replaceAllBtn.textContent = 'Replace All';
  replaceAllBtn.title = 'Replace all matches';
  replaceAllBtn.setAttribute('aria-label', 'Replace all matches');
  replaceAllBtn.style.cssText = prevBtn.style.cssText;
  replaceAllBtn.addEventListener('mouseenter', () => {
    replaceAllBtn.style.backgroundColor = 'var(--ai-toolbar-active-bg)';
    replaceAllBtn.style.borderColor = 'var(--ai-toolbar-active)';
    replaceAllBtn.style.color = 'var(--ai-toolbar-active)';
  });
  replaceAllBtn.addEventListener('mouseleave', () => {
    replaceAllBtn.style.backgroundColor = 'transparent';
    replaceAllBtn.style.borderColor = 'var(--ai-toolbar-input-border)';
    replaceAllBtn.style.color = 'var(--ai-toolbar-text-secondary)';
  });
  replaceRow.appendChild(replaceAllBtn);

  const caseCb = caseOption.input;
  const wordCb = wordOption.input;
  const regexpCb = regexOption.input;

  // Replaces the match count until the query, document or selection changes again
  let notice: { text: string; title: string } | null = null;

  const refreshCount = () => {
    countEl.title = notice?.title ?? '';
    if (notice) {
      countEl.textContent = notice.text;
      countEl.style.display = 'inline';
      return;
    }
    renderMatchCount(view, countEl);
  };

  const clearNotice = () => {
    notice = null;
  };

  // The editor is only read-only while an AI edit waits for Accept/Reject
  const blockedByReadOnly = (): boolean => {
    if (!view.state.readOnly) return false;
    notice = { text: 'AI edit pending', title: 'Accept or reject the AI edit before replacing' };
    refreshCount();
    return true;
  };

  const syncControlsFromState = () => {
    applyQueryToControls(getSearchQuery(view.state), {
      dom,
      searchInput,
      replaceInput,
      caseCb,
      wordCb,
      regexpCb,
      countEl,
      refreshCount,
      clearNotice,
    });
  };

  syncControlsFromState();
  const initialFocus = pendingSearchFocusByView.get(view) ?? 'search';
  pendingSearchFocusByView.delete(view);
  const focusTimer = window.setTimeout(() => {
    if (!dom.isConnected) return;
    const target = initialFocus === 'replace' ? replaceInput : searchInput;
    target.focus();
    target.select();
  }, 0);
  (dom as unknown as { __focusTimer?: number }).__focusTimer = focusTimer;

  const updateQuery = () => {
    const newQuery = new SearchQuery({
      search: searchInput.value,
      caseSensitive: caseCb.checked,
      wholeWord: wordCb.checked,
      regexp: regexpCb.checked,
      replace: replaceInput.value,
    });
    view.dispatch({ effects: setSearchQuery.of(newQuery) });
  };

  [searchInput, replaceInput].forEach(el => el.addEventListener('input', updateQuery));
  [caseCb, wordCb, regexpCb].forEach(el => el.addEventListener('change', updateQuery));

  const step = (backwards: boolean) => {
    if (backwards) findPrevious(view);
    else findNext(view);
    view.dispatch({ scrollIntoView: true });
    refreshCount();
  };

  // replaceNext already replaces the current match and selects the next one
  const replaceCurrent = () => {
    if (blockedByReadOnly()) return;
    notice = null;
    replaceNext(view);
    refreshCount();
  };

  searchInput.addEventListener('keydown', (e) => {
    if (isImeComposing(e) || e.key !== 'Enter') return;
    e.preventDefault();
    step(e.shiftKey);
  });

  replaceInput.addEventListener('keydown', (e) => {
    if (isImeComposing(e) || e.key !== 'Enter') return;
    e.preventDefault();
    replaceCurrent();
  });

  // Keys that work from anywhere in the panel (inputs, option pills, buttons)
  dom.addEventListener('keydown', (e) => {
    if (isImeComposing(e)) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeToolbarSearch(view);
      view.focus();
      return;
    }
    const modG = (e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'g';
    if (e.key === 'F3' || modG) {
      e.preventDefault();
      step(e.shiftKey);
    }
  });

  prevBtn.addEventListener('click', () => step(true));
  nextBtn.addEventListener('click', () => step(false));
  closeBtn.addEventListener('click', () => { closeToolbarSearch(view); view.focus(); });
  replaceBtn.addEventListener('click', replaceCurrent);

  replaceAllBtn.addEventListener('click', () => {
    if (blockedByReadOnly()) return;
    const query = getSearchQuery(view.state);
    let matches = 0;
    if (query.valid) {
      const cursor = query.getCursor(view.state);
      while (!cursor.next().done) matches++;
    }
    notice = replaceAll(view) ? { text: `Replaced ${matches}`, title: '' } : null;
    refreshCount();
  });

  refreshCount();

  return {
    dom,
    searchInput,
    replaceInput,
    caseCb,
    wordCb,
    regexpCb,
    countEl,
    refreshCount,
    clearNotice,
  };
}

function createSearchPanel(view: EditorView) {
  const controls = createSearchPanelControls(view);
  let countRaf = 0;
  const scheduleCount = () => {
    if (countRaf) return;
    countRaf = requestAnimationFrame(() => {
      countRaf = 0;
      controls.refreshCount();
    });
  };
  return {
    dom: controls.dom,
    top: true,
    update(update: ViewUpdate) {
      const prevQuery = getSearchQuery(update.startState);
      const currentQuery = getSearchQuery(update.state);
      const queryChanged = !queryEquals(prevQuery, currentQuery);

      if (queryChanged) {
        applyQueryToControls(currentQuery, controls);
      }

      if (queryChanged || update.docChanged || update.selectionSet) {
        if (!update.transactions.some((tr) => tr.isUserEvent('input.replace'))) {
          controls.clearNotice();
        }
        scheduleCount();
      }
    },
    destroy() {
      if (countRaf) cancelAnimationFrame(countRaf);
      const focusTimer = (controls.dom as unknown as { __focusTimer?: number }).__focusTimer;
      if (focusTimer !== undefined) window.clearTimeout(focusTimer);
      controls.dom.remove();
    },
  };
}

export function toolbarSearch() {
  return [
    search({
      top: true,
      createPanel: (view) => createSearchPanel(view),
    }),
    searchPanelOpen,
    keymap.of([
      { key: 'Mod-f', run: openToolbarSearch },
      // macOS takes Cmd+H (hide app) before the page sees it
      { key: 'Mod-h', mac: 'Mod-Alt-f', run: openToolbarSearchReplace },
      { key: 'F3', run: (view) => stepToolbarSearch(view, false), shift: (view) => stepToolbarSearch(view, true) },
      { key: 'Mod-g', run: (view) => stepToolbarSearch(view, false), shift: (view) => stepToolbarSearch(view, true) },
    ]),
    // Ahead of the default Escape (which collapses a selection first); a no-op when search is closed
    Prec.high(keymap.of([{ key: 'Escape', run: closeToolbarSearch }])),
  ];
}

export function toolbarSearchTheme() {
  return EditorView.theme({
    // Hide CodeMirror's native search UI but keep match highlighting
    '& .cm-search': { display: 'none !important' },
    '& .cm-panels.cm-panels-bottom': { display: 'none !important' },
    '& .cm-searchMatch': {
      backgroundColor: 'var(--vault-search-match-bg)',
      borderRadius: '2px',
      boxShadow: 'inset 0 -1px 0 rgba(146, 64, 14, 0.2)',
    },
    '& .cm-searchMatch-selected': {
      backgroundColor: 'var(--vault-search-match-selected)',
      borderRadius: '2px',
      outline: '1px solid var(--vault-search-match-selected-ring)',
      boxShadow: '0 0 0 1px var(--vault-search-match-selected-ring)',
    },
    // Toolbar search panel — accent tokens follow theme (purple light / cyan dark)
    '& .cm-toolbar-search-panel': {
      backgroundColor: 'var(--ai-toolbar-bg)',
      borderBottom: '1px solid var(--ai-toolbar-border)',
      padding: '8px 12px',
    },
    '& .cm-toolbar-search-panel .search-container': {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    '& .cm-toolbar-search-panel .search-row, & .cm-toolbar-search-panel .replace-row': {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    '& .cm-toolbar-search-panel .search-input, & .cm-toolbar-search-panel .replace-input': {
      flex: '1',
      padding: '6px 10px',
      fontSize: '16px',
      border: '1px solid var(--ai-toolbar-input-border)',
      borderRadius: '6px',
      backgroundColor: 'var(--ai-toolbar-input-bg)',
      color: 'var(--ai-toolbar-text)',
      outline: 'none',
    },
    '& .cm-toolbar-search-panel .search-input:focus, & .cm-toolbar-search-panel .replace-input:focus': {
      borderColor: 'var(--ai-toolbar-active)',
      boxShadow: '0 0 0 2px var(--ai-toolbar-focus-shadow)',
    },
    '& .cm-toolbar-search-panel .search-btn': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 10px',
      fontSize: '13px',
      fontWeight: '500',
      border: '1px solid var(--ai-toolbar-input-border)',
      borderRadius: '6px',
      backgroundColor: 'transparent',
      color: 'var(--ai-toolbar-text-secondary)',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    },
    '& .cm-toolbar-search-panel .search-btn:hover': {
      backgroundColor: 'var(--ai-toolbar-active-bg)',
      borderColor: 'var(--ai-toolbar-active)',
      color: 'var(--ai-toolbar-active)',
    },
    '& .cm-toolbar-search-panel .search-btn.close': {
      fontWeight: '600',
    },
    '& .cm-toolbar-search-panel .search-option': {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      fontSize: '12px',
      fontWeight: '500',
      color: 'var(--ai-toolbar-text-secondary)',
      border: '1px solid var(--ai-toolbar-input-border)',
      borderRadius: '4px',
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'all 0.15s ease',
    },
    '& .cm-toolbar-search-panel .search-option:hover': {
      backgroundColor: 'var(--ai-toolbar-active-bg)',
      borderColor: 'var(--ai-toolbar-active)',
      color: 'var(--ai-toolbar-active)',
    },
    '& .cm-toolbar-search-panel .search-option:has(input:checked)': {
      backgroundColor: 'var(--ai-toolbar-active-bg)',
      borderColor: 'var(--ai-toolbar-active)',
      color: 'var(--ai-toolbar-active)',
      fontWeight: '700',
    },
    '& .cm-toolbar-search-panel .search-option:has(input:focus-visible)': {
      outline: '2px solid var(--ai-toolbar-active)',
      outlineOffset: '1px',
    },
    // Visually hidden but still focusable, so Tab + Space toggle the option
    '& .cm-toolbar-search-panel .search-option input[type="checkbox"]': {
      position: 'absolute',
      width: '1px',
      height: '1px',
      margin: '0',
      opacity: '0',
      pointerEvents: 'none',
    },
    '& .cm-toolbar-search-panel .search-match-count': {
      fontSize: '12px',
      fontWeight: '500',
      color: 'var(--ai-toolbar-text-muted)',
      padding: '0 6px',
    },
  });
}

export default toolbarSearch;
